import React from "react";
import { createPortal } from "react-dom";
import { PartnerPortalTooltipProps } from "../helpers/types";

interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  alt: "Tooltip";
  type: "button";
}

function Button({ alt, type, ...props }: ButtonProps) {
  return <button type="button" {...props} />;
}

const PartnerPortalTooltip = ({
  tooltip,
  children,
  position,
  externalTooltipHandler,
  externalPosition,
}: PartnerPortalTooltipProps) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState({
    top: 0,
    left: 0,
  });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (
      externalTooltipHandler !== null &&
      externalTooltipHandler !== undefined
    ) {
      setShowTooltip(externalTooltipHandler);
    }
  }, [externalTooltipHandler]);

  const handleShowTooltip = (show: boolean) => {
    if (
      externalTooltipHandler === null ||
      externalTooltipHandler === undefined
    ) {
      setShowTooltip(show);
    }
  };

  const calculateNavPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      return {
        top: rect.top + rect.height / 2,
        left: rect.right - 10,
      };
    }
    return { top: 0, left: 0 };
  };

  const calculateDropdownItemPosition = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    };
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    handleShowTooltip(true);

    if (position === "nav") {
      setTooltipPosition(calculateNavPosition());
    } else if (position === "dropdown-item") {
      setTooltipPosition(calculateDropdownItemPosition(e));
    }
  };

  const handleFocus = () => {
    handleShowTooltip(true);

    if (position === "nav") {
      setTooltipPosition(calculateNavPosition());
    }
    // Note: dropdown-item positioning requires mouse coordinates,
    // so we only support nav positioning for focus events
  };

  const getTooltipPosition = () => {
    return externalPosition || tooltipPosition;
  };

  const getContainerClassName = () => {
    return position === "table"
      ? "pp-input__tooltip-button-container-table"
      : "pp-input__tooltip-button-container";
  };

  const getTooltipClassName = () => {
    const baseClass = "pp-input__tooltip-container";
    const positionClasses = {
      left: "pp-input__tooltip-container-left",
      right: "pp-input__tooltip-container-right",
      table:
        "pp-input__tooltip-container-table pp-input__tooltip-container-right",
      dropdown: "pp-input__tooltip-container-dropdown",
      nav: "pp-input__tooltip-container-nav",
    };

    const positionClass =
      positionClasses[position as keyof typeof positionClasses] || "";
    return `${baseClass} ${positionClass}`.trim();
  };

  const getNavTooltipStyle = () => {
    if (position === "nav") {
      const pos = getTooltipPosition();
      return {
        position: "fixed" as const,
        top: pos.top,
        left: pos.left,
        transform: "translateY(-50%)",
        zIndex: 10000,
      };
    }
    return undefined;
  };

  const renderDropdownTooltip = () => {
    const pos = getTooltipPosition();
    return createPortal(
      <div
        ref={tooltipRef}
        className="pp-input__tooltip-container pp-input__tooltip-container-dropdown-item pp-input__tooltip-portal"
        style={{
          top: pos.top,
          left: pos.left,
          transform: "translateY(-50%)",
        }}
        data-position={position}
        data-is-dropdown-item="true"
        role="tooltip"
      >
        <div
          id="tooltip-content"
          className="pp-input__tooltip pp-input__tooltip-portal-content"
        >
          {tooltip}
        </div>
      </div>,
      document.body,
    );
  };

  const renderRegularTooltip = () => {
    return (
      <div
        ref={tooltipRef}
        className={getTooltipClassName()}
        style={getNavTooltipStyle()}
        data-position={position}
        role="tooltip"
      >
        <div id="tooltip-content" className="pp-input__tooltip">
          {tooltip}
        </div>
      </div>
    );
  };

  const renderTooltipContent = () => {
    if (!showTooltip) return null;
    return position === "dropdown-item"
      ? renderDropdownTooltip()
      : renderRegularTooltip();
  };

  const renderTriggerElement = () => {
    if (children) {
      return (
        <button
          type="button"
          className="pp-tooltip-trigger-button"
          aria-describedby={showTooltip ? "tooltip-content" : undefined}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => handleShowTooltip(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleShowTooltip(!showTooltip);
            }
          }}
          onFocus={handleFocus}
          onBlur={() => handleShowTooltip(false)}
        >
          {children}
        </button>
      );
    }

    return (
      <Button
        className="pp-input__tooltip-icon"
        alt="Tooltip"
        type="button"
        aria-describedby={showTooltip ? "tooltip-content" : undefined}
        onMouseEnter={() => handleShowTooltip(true)}
        onMouseLeave={() => handleShowTooltip(false)}
        onFocus={() => handleShowTooltip(true)}
        onBlur={() => handleShowTooltip(false)}
      />
    );
  };

  return (
    <span ref={containerRef} className={getContainerClassName()}>
      {renderTriggerElement()}
      {renderTooltipContent()}
    </span>
  );
};
export default PartnerPortalTooltip;
